'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useRTLUtils } from '@/lib/translation';
import { Headset, Plus, ChevronRight, ChevronLeft, MessageSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingCard } from '@/components/shared/loading-card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { navbarHeaderPillClassName } from '@/components/layout/navbar-header-pill';
import { cn } from '@/lib/utils';
import type { TicketType } from '@/lib/tickets/ticket-type';

type TicketStatus = 'open' | 'waiting_for_client' | 'resolved' | 'rejected';

interface Ticket {
  id: string;
  orgId: string;
  userId: string;
  authorEmail: string | null;
  authorName: string | null;
  subject: string;
  content: string;
  type: TicketType;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  orgEmail?: string | null;
  lastComment: {
    id: string;
    authorEmail: string | null;
    authorName: string | null;
    content: string;
    createdAt: string;
  } | null;
}

interface TicketsPopoverProps {
  isSuperAdmin: boolean;
  isActive: boolean;
}

function getStatusColor(status: TicketStatus) {
  switch (status) {
    case 'open':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'waiting_for_client':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'resolved':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
}

export function TicketsPopover({ isSuperAdmin, isActive }: TicketsPopoverProps) {
  const { t } = useTranslation();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    fetch('/api/tickets')
      .then((res) => (res.ok ? res.json() : { tickets: [] }))
      .then((data) => {
        if (!cancelled) setTickets(data.tickets || []);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleNavigate = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative',
                navbarHeaderPillClassName(isActive, open),
              )}
              aria-label={t('tickets.pageTitle', 'Support Tickets')}
            >
              <Headset className="relative h-4 w-4 shrink-0" />
              <span className="relative hidden whitespace-nowrap md:inline">{t('tickets.title')}</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('tickets.pageTitle', 'Support Tickets')}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className={cn('w-[min(90vw,380px)] p-0', directionClass)}
        align={isRTL ? 'start' : 'end'}
        sideOffset={8}
      >
        <div className="flex flex-col min-h-[min(70vh,460px)] max-h-[min(70vh,720px)]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className={cn('text-sm font-semibold text-gray-900 dark:text-white', textAlignClass)}>
              {t('tickets.pageTitle', 'Support Tickets')}
            </h3>
            <Button
              size="sm"
              variant="default"
              className="h-8 gap-1.5"
              onClick={() => handleNavigate('/tickets/new')}
            >
              <Plus className="h-4 w-4" />
              {t('tickets.createNew')}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="p-4">
                <LoadingCard variant="ghost" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col justify-center items-center h-full min-h-[min(60vh,360px)] px-4 text-center">
                <MessageSquare className="h-10 w-10 text-gray-400 mb-3" />
                <h4 className={cn('text-sm font-semibold text-gray-900 dark:text-white mb-2')}>
                  {t('tickets.noTicketsTitle')}
                </h4>
                <p className={cn('text-xs text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line')}>
                  {t('tickets.noTicketsSubtitle')}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {tickets.map((ticket) => (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      onClick={() => handleNavigate(`/ticket/${ticket.id}`)}
                      className={cn(
                        'w-full px-4 py-3 flex border-b border-gray-200 dark:border-gray-700 items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer',
                        directionClass
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge className={cn('text-xs flex items-center gap-2', getStatusColor(ticket.status))}>
                            {ticket.status === 'open' && (
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
                              </span>
                            )}
                            {t(`tickets.status.${ticket.status}`)}
                          </Badge>
                        </div>
                        <p className={cn('text-sm font-medium text-gray-900 dark:text-white truncate', textAlignClass)}>
                          {ticket.subject}
                        </p>
                        <p className={cn('text-xs text-gray-500 dark:text-gray-400 truncate', textAlignClass)}>
                          {ticket.authorName || ticket.authorEmail || t('tickets.unknownAuthor')}
                          {ticket.commentCount > 0 && ` · ${ticket.commentCount} ${t('tickets.detail.updates')}`}
                        </p>
                      </div>
                      {isRTL ? (
                        <ChevronLeft className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
